import React, { useEffect, useRef, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import VideoPlayer from './VideoPlayer';
import Controls from './Controls';
import ShareLink from './ShareLink';

const Room = () => {
  const { roomId } = useParams();
  const navigate = useNavigate();
  const [localStream, setLocalStream] = useState(null);
  const [remoteStreams, setRemoteStreams] = useState({});
  const [participants, setParticipants] = useState([]);
  const [isAudioMuted, setIsAudioMuted] = useState(false);
  const [isVideoOff, setIsVideoOff] = useState(false);
  const [isSharingScreen, setIsSharingScreen] = useState(false);
  const [messages, setMessages] = useState([]);
  const [newMessage, setNewMessage] = useState('');
  const [isChatOpen, setIsChatOpen] = useState(false);
  
  const userName = useRef(sessionStorage.getItem('userName') || 'Anonymous');
  const userId = useRef(`user-${Math.floor(Math.random() * 1000000)}`);
  const socketRef = useRef(null);
  const peerConnectionsRef = useRef({});
  const localVideoRef = useRef(null);
  
  // STUN/TURN servers configuration for NAT traversal
  const iceServers = {
    iceServers: [
      { urls: 'stun:stun.l.google.com:19302' },
      { urls: 'stun:stun1.l.google.com:19302' },
      { urls: 'stun:stun2.l.google.com:19302' },
      { urls: 'stun:stun3.l.google.com:19302' },
      { urls: 'stun:stun4.l.google.com:19302' },
    ],
  };

  // Initialize WebSocket connection and local media
  useEffect(() => {
    if (!userName.current) {
      alert('Please enter your name first');
      navigate('/');
      return;
    }

    // Connect to signaling server
    const wsUrl = `ws://localhost:8000/ws/${roomId}/${userId.current}`;
    socketRef.current = new WebSocket(wsUrl);

    // Set up WebSocket event handlers
    socketRef.current.onopen = () => {
      console.log('WebSocket connection established');
      // Get local media stream
      initLocalStream();
    };

    socketRef.current.onmessage = (event) => {
      const message = JSON.parse(event.data);
      handleSignalingMessage(message);
    };

    socketRef.current.onerror = (error) => {
      console.error('WebSocket error:', error);
    };

    socketRef.current.onclose = () => {
      console.log('WebSocket connection closed');
    };

    // Clean up on component unmount
    return () => {
      // Close all peer connections
      Object.values(peerConnectionsRef.current).forEach(pc => {
        pc.close();
      });
      
      // Close WebSocket connection
      if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
        socketRef.current.close();
      }
      
      // Stop local stream tracks
      if (localStream) {
        localStream.getTracks().forEach(track => {
          track.stop();
        });
      }
    };
  }, [roomId, navigate]);

  // Initialize local media stream
  const initLocalStream = async () => {
    try {
      // First check if media devices are available
      if (!navigator.mediaDevices || !navigator.mediaDevices.getUserMedia) {
        throw new Error('Media devices API not available in this browser');
      }
      
      // Try to get both audio and video
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });
        
        setLocalStream(stream);
        
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        
        // After getting local stream, request participants list
        sendToServer({
          type: 'get-participants'
        });
      } catch (mediaError) {
        console.warn('Could not access both camera and microphone, trying audio only:', mediaError);
        
        // Try audio only as fallback
        try {
          const audioStream = await navigator.mediaDevices.getUserMedia({
            audio: true,
            video: false
          });
          
          setLocalStream(audioStream);
          setIsVideoOff(true);
          
          alert('Camera access was denied or not available. Joining with audio only.');
          
          // After getting local stream, request participants list
          sendToServer({
            type: 'get-participants'
          });
        } catch (audioError) {
          console.error('Could not access microphone either:', audioError);
          alert('Could not access microphone. You will be in view-only mode.');
          
          // Create empty stream as a last resort
          const emptyStream = new MediaStream();
          setLocalStream(emptyStream);
          setIsAudioMuted(true);
          setIsVideoOff(true);
          
          // Still request participants list
          sendToServer({
            type: 'get-participants'
          });
        }
      }
    } catch (error) {
      console.error('Error accessing media devices:', error);
      alert('Could not access camera or microphone. Please check permissions and make sure you are using a secure (HTTPS) connection or localhost.');
    }
  };

  // Handle incoming signaling messages
  const handleSignalingMessage = async (message) => {
    switch (message.type) {
      case 'user-joined':
        console.log('User joined:', message.userId);
        setParticipants(message.participants);
        
        // If we're not the new user, initiate connection to them
        if (message.userId !== userId.current && localStream) {
          createPeerConnection(message.userId);
        }
        break;
        
      case 'user-left':
        console.log('User left:', message.userId);
        setParticipants(message.participants);
        
        // Clean up peer connection for the user who left
        if (peerConnectionsRef.current[message.userId]) {
          peerConnectionsRef.current[message.userId].close();
          delete peerConnectionsRef.current[message.userId];
          
          // Remove their stream
          setRemoteStreams(prev => {
            const newStreams = { ...prev };
            delete newStreams[message.userId];
            return newStreams;
          });
        }
        break;
        
      case 'offer':
        await handleOffer(message);
        break;
        
      case 'answer':
        await handleAnswer(message);
        break;
        
      case 'ice-candidate':
        await handleIceCandidate(message);
        break;
        
      case 'chat':
        handleChatMessage(message);
        break;
        
      case 'participants':
        // Initialize connections with all existing participants
        message.participants.forEach(participantId => {
          if (participantId !== userId.current && !peerConnectionsRef.current[participantId] && localStream) {
            createPeerConnection(participantId);
          }
        });
        break;
        
      default:
        console.log('Unknown message type:', message.type);
    }
  };

  // Create a new RTCPeerConnection for a specific user
  const createPeerConnection = async (targetUserId) => {
    try {
      const peerConnection = new RTCPeerConnection(iceServers);
      peerConnectionsRef.current[targetUserId] = peerConnection;
      
      // Add local tracks to the peer connection
      localStream.getTracks().forEach(track => {
        peerConnection.addTrack(track, localStream);
      });
      
      // Handle ICE candidates
      peerConnection.onicecandidate = (event) => {
        if (event.candidate) {
          sendToServer({
            type: 'ice-candidate',
            target: targetUserId,
            data: event.candidate
          });
        }
      };
      
      // Handle connection state changes
      peerConnection.onconnectionstatechange = () => {
        console.log(`Connection state with ${targetUserId}:`, peerConnection.connectionState);
      };
      
      // Handle incoming tracks
      peerConnection.ontrack = (event) => {
        console.log('Received remote track from:', targetUserId);
        setRemoteStreams(prev => ({
          ...prev,
          [targetUserId]: event.streams[0]
        }));
      };
      
      // Create and send offer
      const offer = await peerConnection.createOffer();
      await peerConnection.setLocalDescription(offer);
      
      sendToServer({
        type: 'offer',
        target: targetUserId,
        data: offer
      });
      
    } catch (error) {
      console.error('Error creating peer connection:', error);
    }
  };

  // Handle incoming offer
  const handleOffer = async (message) => {
    const senderId = message.sender;
    
    try {
      // Create peer connection if it doesn't exist
      if (!peerConnectionsRef.current[senderId]) {
        const peerConnection = new RTCPeerConnection(iceServers);
        peerConnectionsRef.current[senderId] = peerConnection;
        
        // Add local tracks
        localStream.getTracks().forEach(track => {
          peerConnection.addTrack(track, localStream);
        });
        
        // Handle ICE candidates
        peerConnection.onicecandidate = (event) => {
          if (event.candidate) {
            sendToServer({
              type: 'ice-candidate',
              target: senderId,
              data: event.candidate
            });
          }
        };
        
        // Handle incoming tracks
        peerConnection.ontrack = (event) => {
          console.log('Received remote track from:', senderId);
          setRemoteStreams(prev => ({
            ...prev,
            [senderId]: event.streams[0]
          }));
        };
      }
      
      const peerConnection = peerConnectionsRef.current[senderId];
      
      // Set remote description from offer
      await peerConnection.setRemoteDescription(new RTCSessionDescription(message.data));
      
      // Create and send answer
      const answer = await peerConnection.createAnswer();
      await peerConnection.setLocalDescription(answer);
      
      sendToServer({
        type: 'answer',
        target: senderId,
        data: answer
      });
      
    } catch (error) {
      console.error('Error handling offer:', error);
    }
  };

  // Handle incoming answer
  const handleAnswer = async (message) => {
    const senderId = message.sender;
    
    try {
      const peerConnection = peerConnectionsRef.current[senderId];
      
      if (peerConnection) {
        await peerConnection.setRemoteDescription(new RTCSessionDescription(message.data));
      }
    } catch (error) {
      console.error('Error handling answer:', error);
    }
  };

  // Handle incoming ICE candidate
  const handleIceCandidate = async (message) => {
    const senderId = message.sender;
    
    try {
      const peerConnection = peerConnectionsRef.current[senderId];
      
      if (peerConnection) {
        await peerConnection.addIceCandidate(new RTCIceCandidate(message.data));
      }
    } catch (error) {
      console.error('Error handling ICE candidate:', error);
    }
  };

  // Handle chat messages
  const handleChatMessage = (message) => {
    setMessages(prev => [...prev, {
      sender: message.sender === userId.current ? 'You' : message.sender,
      content: message.message,
      timestamp: message.timestamp || Date.now()
    }]);
  };

  // Send message to signaling server
  const sendToServer = (message) => {
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.send(JSON.stringify(message));
    }
  };

  // Toggle audio mute
  const toggleAudio = () => {
    if (localStream) {
      const audioTracks = localStream.getAudioTracks();
      audioTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsAudioMuted(!isAudioMuted);
    }
  };

  // Toggle video
  const toggleVideo = () => {
    if (localStream) {
      const videoTracks = localStream.getVideoTracks();
      videoTracks.forEach(track => {
        track.enabled = !track.enabled;
      });
      setIsVideoOff(!isVideoOff);
    }
  };

  // Toggle screen sharing
  const toggleScreenShare = async () => {
    if (isSharingScreen) {
      // Switch back to camera
      try {
        const stream = await navigator.mediaDevices.getUserMedia({
          audio: true,
          video: true
        });
        
        // Replace tracks in all peer connections
        Object.values(peerConnectionsRef.current).forEach(pc => {
          const senders = pc.getSenders();
          senders.forEach(sender => {
            if (sender.track.kind === 'video') {
              const videoTrack = stream.getVideoTracks()[0];
              if (videoTrack) {
                sender.replaceTrack(videoTrack);
              }
            }
          });
        });
        
        // Stop old tracks and set new stream
        if (localStream) {
          localStream.getTracks().forEach(track => track.stop());
        }
        
        setLocalStream(stream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        
        setIsSharingScreen(false);
      } catch (error) {
        console.error('Error switching back to camera:', error);
      }
    } else {
      // Switch to screen sharing
      try {
        const stream = await navigator.mediaDevices.getDisplayMedia({
          video: true
        });
        
        // Add audio track from current stream if available
        if (localStream) {
          const audioTrack = localStream.getAudioTracks()[0];
          if (audioTrack) {
            stream.addTrack(audioTrack);
          }
        }
        
        // Replace tracks in all peer connections
        Object.values(peerConnectionsRef.current).forEach(pc => {
          const senders = pc.getSenders();
          senders.forEach(sender => {
            if (sender.track.kind === 'video') {
              const videoTrack = stream.getVideoTracks()[0];
              if (videoTrack) {
                sender.replaceTrack(videoTrack);
              }
            }
          });
        });
        
        // Handle screen sharing stopped by user
        stream.getVideoTracks()[0].onended = () => {
          toggleScreenShare();
        };
        
        // Stop old video tracks
        if (localStream) {
          localStream.getVideoTracks().forEach(track => track.stop());
        }
        
        setLocalStream(stream);
        if (localVideoRef.current) {
          localVideoRef.current.srcObject = stream;
        }
        
        setIsSharingScreen(true);
      } catch (error) {
        console.error('Error sharing screen:', error);
      }
    }
  };

  // Send chat message
  const sendChatMessage = () => {
    if (newMessage.trim()) {
      sendToServer({
        type: 'chat',
        message: newMessage,
        timestamp: Date.now()
      });
      
      setNewMessage('');
    }
  };

  // Leave meeting
  const leaveMeeting = () => {
    // Close all connections
    Object.values(peerConnectionsRef.current).forEach(pc => {
      pc.close();
    });
    
    // Stop local stream tracks
    if (localStream) {
      localStream.getTracks().forEach(track => {
        track.stop();
      });
    }
    
    // Close WebSocket connection
    if (socketRef.current && socketRef.current.readyState === WebSocket.OPEN) {
      socketRef.current.close();
    }
    
    // Navigate back to home
    navigate('/');
  };

  return (
    <div className="container" style={{ height: '100vh', padding: 0 }}>
      <div className="flex justify-between items-center p-4" style={{ flexWrap: 'wrap', gap: '0.5rem' }}>
        <div>
          <h2 style={{ color: 'var(--primary-color)', fontWeight: 'bold' }}>CZone</h2>
          <p style={{ fontSize: '0.8rem' }}>Room ID: {roomId.substring(0, 8)}...</p>
        </div>
        <div className="flex gap-2">
          {/* Share Link Component */}
          <ShareLink roomId={roomId} />
          <button 
            className="btn btn-secondary"
            onClick={() => setIsChatOpen(!isChatOpen)}
          >
            {isChatOpen ? 'Hide Chat' : 'Show Chat'}
          </button>
        </div>
      </div>
      
      <div className="flex" style={{ height: 'calc(100vh - 150px)' }}>
        <div className={`video-container ${isChatOpen ? 'flex-1' : 'w-full'}`}>
          {/* Local video */}
          <div className="video-wrapper">
            <video
              ref={localVideoRef}
              autoPlay
              muted
              playsInline
            />
            <div className="user-name">{userName.current} (You)</div>
          </div>
          
          {/* Remote videos */}
          {Object.entries(remoteStreams).map(([peerId, stream]) => (
            <VideoPlayer
              key={peerId}
              stream={stream}
              userName={`Participant ${peerId}`}
            />
          ))}
        </div>
        
        {/* Chat panel */}
        {isChatOpen && (
          <div className="flex flex-col p-4" style={{ width: '300px', borderLeft: '1px solid #e5e7eb' }}>
            <h3>Chat</h3>
            <div className="flex-1 mb-4" style={{ overflowY: 'auto', height: 'calc(100vh - 250px)' }}>
              {messages.map((msg, index) => (
                <div key={index} className="mb-2">
                  <strong>{msg.sender}:</strong> {msg.content}
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <input
                type="text"
                className="form-control"
                value={newMessage}
                onChange={(e) => setNewMessage(e.target.value)}
                onKeyPress={(e) => e.key === 'Enter' && sendChatMessage()}
                placeholder="Type a message..."
              />
              <button className="btn btn-primary" onClick={sendChatMessage}>
                Send
              </button>
            </div>
          </div>
        )}
      </div>
      
      {/* Controls */}
      <Controls
        isAudioMuted={isAudioMuted}
        isVideoOff={isVideoOff}
        isSharingScreen={isSharingScreen}
        toggleAudio={toggleAudio}
        toggleVideo={toggleVideo}
        toggleScreenShare={toggleScreenShare}
        leaveMeeting={leaveMeeting}
      />
    </div>
  );
};

export default Room;
